import * as React from "react";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { ClientURL } from "api";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import Icon from "components/Global/icon";

const textField = "display";
const keyField = "CustomerID";
const emptyItem = {
  [textField]: "loading ...",
  CustomerID: 0,
};
const pageSize = 10;
const loadingData = [];
while (loadingData.length < pageSize) {
  loadingData.push({
    ...emptyItem,
  });
}
const baseUrl = ClientURL;

const ClientComboBox = (props) => {
  const { accessToken } = useOidcAccessToken();
  const init = {
    method: "GET",
    accept: "application/json",
    headers: {
      authorization: `Bearer ${accessToken}`,
    },
  };
  const dataCaching = useRef([]);
  const pendingRequest = useRef();
  const requestStarted = useRef(false);
  const [data, setData] = useState([]);
  const [value, setValue] = useState(null);
  const [error, setError] = useState(false);
  const [errorData, setErrorData] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const skipRef = useRef(0);
  const resetCach = () => {
    dataCaching.current.length = 0;
  };
  const requestData = useCallback((skip, filter) => {
    if (requestStarted.current) {
      clearTimeout(pendingRequest.current);
      pendingRequest.current = setTimeout(() => {
        requestData(skip, filter);
      }, 50);
      return;
    }

    const search =
      filter !== undefined && filter !== "" ? `search=${filter}` : "";
    const url = `${baseUrl}${search}&page=${skip + 1
      }&limit=${pageSize}&active=true&orderby=clientRef&fields=clientid,clientRef,clientName,branchName,phoneNo,email,address,type,noOfMatters,feeEarnerRef`;

    requestStarted.current = true;
    setData(loadingData);

    fetch(url, init)
      .then((response) => {
        if (!response.ok) return Promise.reject(response);
        return response.json();
      })
      .then((json) => {
        const items = json.data.map((element) => ({
          ...element,
          [keyField]: element.clientId,
          [textField]: element.display,
          CustomerRef: element.clientRef,
          ContactName: element.clientName,
        }));

        items.forEach((item, index) => {
          dataCaching.current[index + skip] = item;
        });

        if (skip === skipRef.current) {
          setData(items);
        }
      })
      .catch((error) => {
        setError(true);
        setData([]);
      })
      .finally(() => {
        requestStarted.current = false;
      });
  }, []);

  useEffect(() => {
    resetCach();
    skipRef.current = 0;
    requestData(0, filter);
    return () => {
      resetCach();
    };
  }, [filter, requestData]);

  const filteredData = useMemo(() => {
    const excludeSet = new Set(props.excludeIds || []);
    if (excludeSet.size === 0) return data;
    return data.filter((item) => !excludeSet.has(item[keyField]));
  }, [data, props.excludeIds]);

  const onFilterChange = useCallback((event) => {
    let filterValue = event.filter.value;
    if (filterValue?.includes(" - ")) {
      filterValue = filterValue.split(" - ")[1] || filterValue.split(" - ")[0];
    }
    resetCach();

    skipRef.current = 0;
    setFilter(filterValue);
  }, []);

  const shouldRequestData = useCallback((skip) => {
    for (let i = 0; i < pageSize; i++) {
      if (!dataCaching.current[skip + i]) {
        return true;
      }
    }
    return false;
  }, []);

  const getCachedData = useCallback((skip) => {
    const data = [];
    for (let i = 0; i < pageSize; i++) {
      data.push(
        dataCaching.current[i + skip] || {
          ...emptyItem,
        }
      );
    }
    return data;
  }, []);
  const pageChange = React.useCallback(
    (event) => {
      const newSkip = event.page.skip;
      if (isNaN(newSkip)) {
        return;
      }
      if (shouldRequestData(newSkip)) {
        requestData(newSkip, filter);
      }
      const data = getCachedData(newSkip);
      setData(data);
      skipRef.current = newSkip;
    },
    [getCachedData, requestData, shouldRequestData, filter]
  );

  const onChange = useCallback((event) => {
    const value = event.target.value;

    if (value && value[textField] === emptyItem[textField]) {
      return;
    }
    setValue(value);
    props.onChangeClient(value);
  }, []);

  useEffect(() => {
    if (props.val) {
      const searchValue = props.val?.ContactName || props.val?.CustomerRef;
      if (searchValue) {
        onFilterChange({
          filter: {
            value: searchValue,
          },
        });
      }
      setValue(props.val);
    }
    if (props.val && data.length === 0) {
      setErrorData([props.val]);
    }
  }, [props.val]);
  return (
    <div>
      <ComboBox
        iconClassName="wa"
        icon={<Icon icon="arrow-down" />}
        data={error ? errorData : filteredData}
        required={props.required ?? true}
        value={value}
        onChange={onChange}
        dataItemKey={keyField}
        textField={textField}
        filterable={true}
        disabled={props.disabled}
        onFilterChange={onFilterChange}
        virtual={{
          pageSize: pageSize,
          skip: skipRef.current,
          total: total,
        }}
        onPageChange={pageChange}
        placeholder="Select Client"
        style={{
          width: props.width || "326px",
          maxWidth: "400px",
          height: "32px",
          boxShadow: props.boxShadow && "0px 1px 9px rgba(0, 0, 0, 0.12)",
        }}
      />
    </div>
  );
};

export default ClientComboBox;
